<template>
  <b-modal
    v-model="visivel"
    :title="modoEdicao ? 'Editar Budget' : 'Novo Budget'"
    size="lg"
    no-close-on-backdrop
    hide-footer
    @hidden="resetar"
  >
    <div v-if="carregando" class="text-center py-4"><b-spinner /></div>
    <b-form v-else @submit.prevent="salvar">
      <b-row>
        <b-col md="6">
          <b-form-group label="Supervisor *">
            <v-select
              v-model="form.supervisorid"
              :options="supervisores"
              :reduce="s => s.supervisorid"
              label="supervisornome"
              :disabled="modoEdicao"
              placeholder="Pesquisar supervisor..."
              @input="onSupervisorChange"
            >
              <template #no-options>Nenhum supervisor encontrado</template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Categoria *">
            <b-form-select v-model="form.categoria">
              <b-form-select-option value="ELT">ELT — English Language Teaching</b-form-select-option>
              <b-form-select-option value="GL">GL — Global Languages</b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Valor Total *">
            <b-form-input v-model="form.valor" type="text" inputmode="decimal" placeholder="Ex.: 1500,50"
              @input="form.valor = mascaraMoedaBR(form.valor)" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Data Início *">
            <b-form-input v-model="form.datainicio" type="date" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Data Término *">
            <b-form-input v-model="form.datatermino" type="date" />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group label="Descrição">
            <b-form-input v-model="form.descricao" maxlength="255" />
          </b-form-group>
        </b-col>
        <b-col v-if="modoEdicao" md="12">
          <b-form-checkbox v-model="form.ativo" switch class="mb-1">
            Budget ativo
          </b-form-checkbox>
        </b-col>
      </b-row>

      <hr />
      <div class="d-flex align-items-center mb-1">
        <h6 class="mb-0">Alocação por representante (opcional)</h6>
        <b-badge variant="light-primary" class="ml-1">
          Alocado: {{ formatarMoeda(somaAlocacoes) }}
        </b-badge>
        <b-badge :variant="restante < 0 ? 'light-danger' : 'light-secondary'" class="ml-1">
          Restante: {{ formatarMoeda(restante) }}
        </b-badge>
      </div>

      <div v-if="!form.supervisorid" class="text-muted small mb-1">
        Selecione um supervisor para distribuir o valor entre seus representantes.
      </div>
      <div v-else-if="!representantes.length" class="text-muted small mb-1">
        Este supervisor não possui representantes vinculados.
      </div>
      <b-table
        v-else
        :items="alocacaoRows"
        :fields="alocFields"
        small
        responsive
        class="mb-1"
      >
        <template #cell(representantenome)="data">
          {{ data.item.representantenome }}
          <b-badge v-if="!data.item.representanteativo" variant="light-secondary" class="ml-25">inativo</b-badge>
        </template>
        <template #cell(valor)="data">
          <b-form-input
            v-model="alocacaoMap[data.item.representanteid]"
            type="text"
            inputmode="decimal"
            size="sm"
            style="max-width:160px"
            @input="mascararCampoAlocacao(data.item.representanteid)"
          />
        </template>
      </b-table>

      <div class="text-right mt-2">
        <b-button variant="outline-secondary" class="mr-1" @click="visivel = false">Cancelar</b-button>
        <b-button variant="relief-success" :disabled="isSaving" type="submit">
          <b-spinner v-if="isSaving" small class="mr-25" />
          Salvar
        </b-button>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import {
  BModal, BForm, BRow, BCol, BFormGroup,
  BFormInput, BFormCheckbox, BFormSelect, BFormSelectOption, BButton, BSpinner, BTable, BBadge,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import { mapActions, mapState } from 'vuex';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { parseDecimalBR, formatDecimalBR, mascaraMoedaBR } from '@/libs/numero';

export default {
  name: 'BudgetModal',
  components: {
    BModal, BForm, BRow, BCol, BFormGroup,
    BFormInput, BFormCheckbox, BFormSelect, BFormSelectOption, BButton, BSpinner, BTable, BBadge,
    vSelect,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      visivel: false,
      carregando: false,
      modoEdicao: false,
      budgetId: null,
      form: this.formVazio(),
      alocacaoMap: {},
      alocFields: [
        { key: 'representantenome', label: 'Representante' },
        { key: 'valor', label: 'Valor alocado', class: 'text-right' },
      ],
    };
  },
  computed: {
    ...mapState('Budget', ['supervisores', 'representantes', 'isSaving']),
    alocacaoRows() {
      return this.representantes.map(r => ({
        representanteid: r.representanteid,
        representantenome: r.representantenome,
        representanteativo: r.representanteativo,
      }));
    },
    somaAlocacoes() {
      return Object.values(this.alocacaoMap).reduce((s, v) => s + parseDecimalBR(v), 0);
    },
    restante() {
      return parseDecimalBR(this.form.valor) - this.somaAlocacoes;
    },
  },
  methods: {
    formatDecimalBR,
    mascaraMoedaBR,
    // Máscara ao vivo (dígitos entram pelos centavos; sempre exibe "0,00"+).
    mascararCampoAlocacao(representanteid) {
      const atual = this.alocacaoMap[representanteid];
      const mascarado = mascaraMoedaBR(atual);
      if (mascarado !== atual) this.$set(this.alocacaoMap, representanteid, mascarado);
    },
    ...mapActions('Budget', ['carregarSupervisores', 'carregarRepresentantes', 'buscarPorId', 'criar', 'atualizar']),
    formVazio() {
      return {
        supervisorid: null,
        categoria: 'ELT',
        valor: '0,00',
        datainicio: '',
        datatermino: '',
        descricao: '',
        ativo: true,
      };
    },
    async abrir(id = null) {
      this.resetar();
      this.visivel = true;
      this.carregando = true;
      try {
        if (!this.supervisores.length) await this.carregarSupervisores();
        if (id) {
          this.modoEdicao = true;
          this.budgetId = id;
          const b = await this.buscarPorId(id);
          this.form = {
            supervisorid: b.supervisorid,
            categoria: b.categoria || 'ELT',
            valor: formatDecimalBR(b.valor) || '0,00',
            datainicio: this.isoDate(b.datainicio),
            datatermino: this.isoDate(b.datatermino),
            descricao: b.descricao || '',
            ativo: !!b.ativo,
          };
          await this.carregarRepresentantes(b.supervisorid);
          const map = {};
          (b.alocacoes || []).forEach(a => { map[a.representanteid] = formatDecimalBR(a.valor) || '0,00'; });
          // Reps sem alocação prévia começam com a máscara zerada.
          (this.representantes || []).forEach((r) => {
            if (!(r.representanteid in map)) map[r.representanteid] = '0,00';
          });
          this.alocacaoMap = map;
        }
      } finally {
        this.carregando = false;
      }
    },
    async onSupervisorChange() {
      this.alocacaoMap = {};
      await this.carregarRepresentantes(this.form.supervisorid);
      const map = {};
      (this.representantes || []).forEach((r) => { map[r.representanteid] = '0,00'; });
      this.alocacaoMap = map;
    },
    montarAlocacoes() {
      const out = [];
      Object.entries(this.alocacaoMap).forEach(([representanteid, valor]) => {
        const v = parseDecimalBR(valor);
        if (v > 0) out.push({ representanteid, valor: v });
      });
      return out;
    },
    async salvar() {
      if (!this.form.supervisorid) {
        return this.aviso('Selecione um supervisor');
      }
      if (!this.form.datainicio || !this.form.datatermino) {
        return this.aviso('Informe o período (início e término)');
      }
      if (!(parseDecimalBR(this.form.valor) > 0)) {
        return this.aviso('Informe um valor maior que zero');
      }
      if (this.restante < -0.00001) {
        return this.aviso('A soma das alocações excede o valor total');
      }
      const payload = {
        supervisorid: this.form.supervisorid,
        categoria: this.form.categoria || 'ELT',
        valor: parseDecimalBR(this.form.valor),
        datainicio: this.form.datainicio,
        datatermino: this.form.datatermino,
        descricao: this.form.descricao || null,
        ativo: this.form.ativo,
        alocacoes: this.montarAlocacoes(),
      };
      try {
        if (this.modoEdicao) {
          await this.atualizar({ id: this.budgetId, payload });
        } else {
          await this.criar(payload);
        }
        this.sucesso(this.modoEdicao ? 'Budget atualizado' : 'Budget criado');
        this.visivel = false;
        this.$emit('salvo');
      } catch (e) {
        // erro já exibido pelo client
      }
    },
    resetar() {
      this.modoEdicao = false;
      this.budgetId = null;
      this.form = this.formVazio();
      this.alocacaoMap = {};
    },
    isoDate(d) {
      if (!d) return '';
      return String(d).substring(0, 10);
    },
    formatarMoeda(v) {
      return (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    aviso(title) {
      this.toast({ component: ToastificationContent, props: { title, icon: 'AlertTriangleIcon', variant: 'warning' } });
    },
    sucesso(title) {
      this.toast({ component: ToastificationContent, props: { title, icon: 'CheckCircleIcon', variant: 'success' } });
    },
  },
};
</script>
